import React, {useEffect, useRef} from 'react';
import {Box, Stack, styled} from '@mui/material';
import type {Avatar} from '@contentlab/api/avatars';
import SampleAssetDisplay from '@contentlab/components/sample-asset-display';
import {useVirtualizedGrid} from '@contentlab/hooks/use-virtualized-grid';
import {contentLabActions} from '@contentlab/stores/contentLabStore';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import EmptyFilterationResults from '@contentlab/views/contentlab/pages/ai-avatar-page/components/ai-avatar-editor/subcomponents/EmptyFilterationResults';
import {PREVIEW_AVATAR_POSITION_CORRECTION} from '@contentlab/views/contentlab/pages/ai-avatar-page/constants';
import translate from '@/utils/translate';
import Skeleton from '@/components/skeleton';

const MIN_NUMBER_OF_AVATARS = 8;
const AVATAR_SCROLL_TOP_KEY = 'avatarScrollTop';
const TARGET_COLUMNS = 4;
const DEFAULT_AVATAR_WIDTH = 170;
const DEFAULT_AVATAR_HEIGHT = 265;
const AVATAR_GAP = 16;
const CONTAINER_PADDING = 16;

interface AvatarSelectorProps {
  testID?: string;
  onAvatarSelect?: (avatar: Avatar) => void;
  selectedAvatar?: Avatar | null;
  isAvatarPreviewMuted?: boolean;
  onAvatarPreviewMuteChange?: (avatar: Avatar, muted: boolean) => void;
  avatars: Avatar[];
  isLoading?: boolean;
}

const Container = styled(Box)(() => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
  height: '100%',
  width: '100%',
  ...scrollbarMixins.overrideScrollbarHover(),
}));

function AvatarSelector({
  testID = 'avatar-selector',
  onAvatarSelect,
  selectedAvatar,
  isAvatarPreviewMuted,
  onAvatarPreviewMuteChange,
  avatars,
  isLoading,
}: AvatarSelectorProps) {
  const onAvatarSelectProxy = useRef(onAvatarSelect);
  onAvatarSelectProxy.current = onAvatarSelect;

  // Use the virtualized grid hook
  const {
    parentRef,
    itemSize,
    handleScroll,
    getTotalSize,
    renderVirtualizedItems,
  } = useVirtualizedGrid({
    items: avatars,
    scrollTopKey: AVATAR_SCROLL_TOP_KEY,
    targetColumns: TARGET_COLUMNS,
    containerPadding: CONTAINER_PADDING,
    gap: AVATAR_GAP,
    additionalRowGap: AVATAR_GAP,
    defaultItemWidth: DEFAULT_AVATAR_WIDTH,
    defaultItemHeight: DEFAULT_AVATAR_HEIGHT,
    itemStyle: {
      paddingInline: '4px',
      paddingTop: '4px',
    },
  });

  useEffect(() => {
    const [firstAvatar] = avatars;
    if (firstAvatar && !selectedAvatar) {
      onAvatarSelectProxy.current?.(firstAvatar);
    }
  }, [avatars, selectedAvatar]);

  const handleAvatarClick = (avatar: Avatar) => {
    onAvatarSelect?.(avatar);
  };

  if (isLoading) {
    return (
      <Container data-testid={testID}>
        <Stack sx={{gap: '16px', paddingBottom: '16px'}}>
          <Box sx={{display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
            {Array.from({length: MIN_NUMBER_OF_AVATARS}, (_, i) => (
              <Skeleton
                key={i}
                width={itemSize.width}
                height={itemSize.height}
                variant="rounded"
              />
            ))}
          </Box>
        </Stack>
      </Container>
    );
  }

  if (!isLoading && avatars.length === 0) {
    return (
      <EmptyFilterationResults
        title={translate('No exact matches')}
        subtitle={translate('Adjust your filters or browse the full library')}
        ctaText={translate('Clear All Filters')}
        onCtaClick={() => contentLabActions.resetSelectedAvatarFilters()}
      />
    );
  }

  return (
    <Container ref={parentRef} data-testid={testID} onScroll={handleScroll}>
      <Box
        style={{
          height: `${getTotalSize()}px`,
          width: '100%',
          position: 'relative',
          paddingBottom: '16px',
          boxSizing: 'border-box',
        }}
      >
        {renderVirtualizedItems((avatar) => (
          <Box
            sx={{
              'width': '100%',
              'height': '100%',
              'textAlign': 'left',
              '& .MuiStack-root': {
                alignItems: 'flex-start !important',
              },
              '& .MuiStack-root > *:last-child': {
                alignSelf: 'flex-start !important',
                textAlign: 'left !important',
                width: '100%',
              },
            }}
          >
            <SampleAssetDisplay
              isSelected={avatar.id === selectedAvatar?.id}
              setSelectedAsset={() => handleAvatarClick(avatar)}
              width={itemSize.width}
              height={itemSize.height}
              iconSize="medium"
              overrideMuted={isAvatarPreviewMuted}
              onMuteChange={(muted) =>
                onAvatarPreviewMuteChange?.(avatar, muted)
              }
              preloadVideo={false}
              asset={{
                id: avatar.id,
                title: avatar.name,
                image: avatar.thumbnail,
                video: avatar.videoPreview,
                positionCorrection: PREVIEW_AVATAR_POSITION_CORRECTION,
              }}
            />
          </Box>
        ))}
      </Box>
    </Container>
  );
}

export default AvatarSelector;
